<script lang="ts">
	import { tokensAllowedForDeposit, tokensAllowedForDepositUI } from '$lib/home/scwallet/config';
	import {
		addFundsAmount,
		addFundsInsufficientBalance,
		processingFundDeposit,
		processingFundWithdraw,
		withdrawFundsAmount,
		withdrawFundsInsufficientBalance
	} from '$lib/home/scwallet/store';
	import { nativeBalancesEnriched, userBalancesEnriched } from '$lib/home/store';
	import { getTokenLogo } from '$lib/tokensConfig';
	import type { TokenSymbol } from '$lib/types/tokens';
	import { getDecimalOfToken, getNumBackInUnits, getPrecisionOfToken } from '$lib/util/util';
	import { scale } from 'svelte/transition';
	import { depositOrWithdrawPopupToken } from '../store';
	import AmountInput from './AmountInput.svelte';
	import ExecuteButton from './ExecuteButton.svelte';
	import { executeAddFunds, executeWithdrawFunds } from './depositOrWithdraw';
	import { quadInOut } from 'svelte/easing';
	import { onMount } from 'svelte';

	let showMore = false;
	let input: string = '';
	export let componentType: 'Deposit' | 'Withdraw' = 'Deposit';

	let tokenAllowed = true;

	onMount(() => {
		$tokensAllowedForDepositUI.forEach((tokenUI) => {
			if (tokenUI.symbol === $depositOrWithdrawPopupToken) {
				tokenAllowed = tokenUI.allowed;
			}
		});
	});

	function onSelectToken(token: TokenSymbol) {
		depositOrWithdrawPopupToken.set(token);
		showMore = false;
		$tokensAllowedForDepositUI.forEach((tokenUI) => {
			if (tokenUI.symbol === token) {
				tokenAllowed = tokenUI.allowed;
			}
		});
	}
</script>

<div class="text-fx-grey-200 text-xs pb-2">Select Asset:</div>

<div class="relative">
	<button
		class="flex w-full bg-fx-black-20 border-fx-grey-600 border rounded-lg squircle-lg justify-between items-center px-3 py-2.5"
		on:click={() => (showMore = !showMore)}
		on:keydown={() => (showMore = !showMore)}
	>
		<div class="flex justify-start">
			<img
				loading="lazy"
				src={getTokenLogo($depositOrWithdrawPopupToken)}
				alt="usdc.svg"
				class="w-5 h-5 mr-2"
			/>
			<div class="text-fx-grey-200 text-sm">{$depositOrWithdrawPopupToken}</div>
		</div>
		<img
			loading="lazy"
			src={'down-arrow.png'}
			alt=""
			class="w-3 transition-transform duration-300 ease-in-out h-min"
			class:flip-arrow={showMore}
		/>
	</button>

	{#if showMore}
		<div
			class="w-full z-50 absolute bg-fx-gradient-8 border-fx-grey-500 border mt-1 rounded-lg squircle-lg h-40 flex flex-col justify-between gap-2 p-2 overflow-y-auto fx-scrollbar"
			transition:scale|global={{
				duration: 100,
				delay: 0,
				opacity: 0.9,
				start: 0.97,
				easing: quadInOut
			}}
		>
			{#each $tokensAllowedForDeposit as token}
				<button
					class="flex w-full justify-between items-center bg-fx-grey-700 rounded-md squircle-md p-2"
					on:click={() => onSelectToken(token)}
					on:keydown={() => onSelectToken(token)}
				>
					<div class="flex gap-1">
						<img loading="lazy" src={getTokenLogo(token)} alt="susd.svg" class="w-5 h-5" />
						<p class="text-fx-white">{token}</p>
					</div>

					<div class="flex gap-1 text-xs">
						<p class="text-fx-grey-400">Balance:</p>
						<p class="text-fx-blue">
							{#if $nativeBalancesEnriched && $nativeBalancesEnriched[token] && componentType === 'Deposit'}
								<span class="text-fx-blue text-xs"
									>{getNumBackInUnits(
										$nativeBalancesEnriched[token].balance,
										getDecimalOfToken(token),
										getPrecisionOfToken(token)
									)}</span
								>
							{:else if $userBalancesEnriched && $userBalancesEnriched[token] && componentType === 'Withdraw'}
								<span class="text-fx-blue text-xs"
									>{getNumBackInUnits(
										$userBalancesEnriched[token].balance,
										getDecimalOfToken(token),
										getPrecisionOfToken(token)
									)}</span
								>
							{:else}
								<span class="text-fx-grey-200 text-xs">--</span>
							{/if}
						</p>
					</div>
				</button>
			{/each}
		</div>
	{/if}
</div>

<AmountInput selectedToken={$depositOrWithdrawPopupToken} {componentType} bind:input />

<div class="border-t border-t-fx-grey-600 w-full my-4" />

{#if componentType == 'Deposit'}
	<ExecuteButton
		bind:input
		fundsAmount={$addFundsAmount}
		validation={$addFundsInsufficientBalance}
		processing={$processingFundDeposit}
		execute={executeAddFunds}
		type={'Deposit'}
		token={$depositOrWithdrawPopupToken}
		allowed={tokenAllowed}
	/>
{:else if componentType == 'Withdraw'}
	<ExecuteButton
		bind:input
		fundsAmount={$withdrawFundsAmount}
		validation={$withdrawFundsInsufficientBalance}
		processing={$processingFundWithdraw}
		execute={executeWithdrawFunds}
		type={'Withdraw'}
		token={$depositOrWithdrawPopupToken}
		allowed={tokenAllowed}
	/>
{/if}

<style>
	.flip-arrow {
		transform: rotate(180deg);
	}
</style>
